import { useMemo, useRef } from 'react';

const DEFAULT_THRESHOLD_SEC = 180;   // 3 minuty dojezdu autem
const HYSTEREZE_SEC = 25;            // dovnitř pod 180, ven nad 205 — proti flickerování
const FALLBACK_RADIUS_M = 800;       // pokud Mapy.com routing zatím nevrátil ETA

/**
 * Detekuje, jestli je řidič „za rohem" — primárně podle **dojezdového času**
 * z Mapy.com routing (`routeDurationSec`), s haversine fallbackem pokud routing
 * ještě nedoběhl.
 *
 * @param poloha            {lat,lng} aktuální GPS řidiče
 * @param cil               {lat,lng} aktuální zastávky
 * @param routeDurationSec  reálná doba jízdy (sec) z `useRouteGeometry` (může být null)
 * @param thresholdSec      hranice „už za rohem" (default 180 = 3 min)
 *
 * @return {{ inZone: boolean, distanceM: number|null, etaSec: number|null }}
 */
export default function useGeofence(poloha, cil, routeDurationSec = null, thresholdSec = DEFAULT_THRESHOLD_SEC) {
    // Hystereze — minulý stav držíme v ref, ať komponenta neflikruje na hranici.
    const lastInZoneRef = useRef(false);

    return useMemo(() => {
        if (!poloha?.lat || !cil?.lat) {
            lastInZoneRef.current = false;
            return { inZone: false, distanceM: null, etaSec: null };
        }

        const distM = haversineM(poloha.lat, poloha.lng, cil.lat, cil.lng);

        // Pokud máme dojezdový čas z routingu, používáme ho jako primární zdroj.
        // Bez něho fallback na vzdušnou vzdálenost (cca odpovídající 3 min při 35 km/h).
        let inZone;
        if (routeDurationSec != null) {
            const dolniPrah = thresholdSec;                  // 180 s
            const horniPrah = thresholdSec + HYSTEREZE_SEC;  // 205 s
            inZone = lastInZoneRef.current
                ? routeDurationSec <= horniPrah   // už uvnitř → vystoupíme až nad horní práh
                : routeDurationSec <= dolniPrah;  // venku → vstoupíme až pod dolní práh
        } else {
            inZone = distM <= FALLBACK_RADIUS_M;
        }

        lastInZoneRef.current = inZone;
        return {
            inZone,
            distanceM: Math.round(distM),
            etaSec: routeDurationSec ?? null,
        };
    }, [poloha?.lat, poloha?.lng, cil?.lat, cil?.lng, routeDurationSec, thresholdSec]);
}

function haversineM(lat1, lng1, lat2, lng2) {
    const R = 6371000;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180)
        * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
